<template>
  <div class="diagnosis-draft">
    <div class="shadow-sm p-3 mb-4 bg-light">
      <strong class="d-block mb-3">
        <template v-if="diagnosisType">{{ $t(diagnosisType) }}</template>
        {{ $t("diagnosis") }}
      </strong>
      <table class="table table-hover table-bordered">
        <thead>
          <tr>
            <th>ICD10</th>
            <th>{{ $t("name") }}</th>
          </tr>
        </thead>
        <tbody v-if="diagnosis">
          <tr v-for="c of diagnosis.conditions" :key="c.code">
            <td>
              {{ c.code }}
            </td>
            <td>
              {{ c.name[authLocale] }}
            </td>
            <td v-if="!diagnosis.published_at">
              <a
                class="pointer svg"
                @click.prevent="removeDisease(diagnosis.id, c.code)"
              >
                <span class="text-primary">
                  <remove-icon></remove-icon>
                </span>
              </a>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr v-if="!diagnosis || !diagnosis.published_at">
            <td colspan="3">
              <input
                type="text"
                class="w-100 px-3 py-2"
                v-model="keyword"
                :placeholder="$t('search')"
              />
              <div class="disease-list border-bottom">
                <div
                  class="list row no-gutters"
                  v-for="disease of diseasesForSelect"
                  :key="`disease-${disease.code}`"
                >
                  <div class="item item-last col">
                    <a
                      class="py-2 px-3 pointer"
                      @click.prevent="addDisease(diagnosisId, disease.code)"
                    >
                      {{ disease.name[authLocale] }} ({{ disease.code }})
                    </a>
                  </div>
                </div>
              </div>
            </td>
          </tr>
          <tr>
            <td colspan="3">
              <label
                class="w-100"
                :for="`case-diagnosis-comment-${diagnosisId}`"
              >
                <strong class="d-block pb-2">{{ $t("comment") }}</strong>
                <textarea
                  type="text"
                  class="w-100 h-100 form-control"
                  :id="`case-diagnosis-comment-${diagnosisId}`"
                  @blur="saveComment(diagnosisId)"
                  @input="(e) => (comment = e.target.value)"
                  :value="comment"
                ></textarea>
              </label>
            </td>
          </tr>
          <tr v-if="!diagnosisId">
            <td>
              <div class="form-check form-check-inline">
                <input
                  class="form-check-input"
                  type="radio"
                  value="preliminary"
                  v-model="diagnosisType"
                />
                <label class="form-check-label" for="defaultCheck1">
                  {{ $t("preliminary") }}
                </label>
              </div>
              <div class="form-check form-check-inline">
                <input
                  class="form-check-input"
                  type="radio"
                  value="final"
                  v-model="diagnosisType"
                />
                <label class="form-check-label" for="defaultCheck1">
                  {{ $t("final") }}
                </label>
              </div>
            </td>
          </tr>
          <tr v-if="diagnosis && !diagnosis.published_at">
            <td colspan="3">
              <button
                class="btn btn-primary btn-block"
                @click.prevent="publish(diagnosisId)"
              >
                {{ $t("publish") }}
              </button>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>
<script>
import { mapGetters, mapMutations } from 'vuex'
import RemoveIcon from '@/components/Icons/Remove'
export default {
  components: {
    RemoveIcon
  },
  props: {
    value: {
      type: String,
      required: true
    },
    itemId: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      disease: null,
      diagnosisType: null,
      diagnosisId: null,
      showDiseases: false,
      comment: '',
      diseasesForSelect: [],
      keyword: null,
      searching: false
    }
  },
  computed: {
    ...mapGetters({
      authLocale: 'auth/locale',
      diagnosisById: 'pcase/diagnosisById',
      caseById: 'pcase/byId'
    }),
    diagnosis () {
      if (!this.diagnosisId) {
        return null
      }
      return this.diagnosisById(this.value)(this.diagnosisId)
    },
    pcase () {
      return this.caseById(this.value)
    }
  },
  watch: {
    keyword (query) {
      this.diseasesForSelect = []
      if (this.searching) {
        clearTimeout(this.searching)
      }
      this.searching = setTimeout(
        this.searchDiseasesAjax(query, (data) => {
          this.diseasesForSelect = data
        }),
        500
      )
    }
  },
  mounted () {
    this.diagnosisType = this.type
    this.diagnosisId = this.itemId
    if (this.diagnosis) {
      this.comment = this.diagnosis.comment
    }
  },
  methods: {
    ...mapMutations({
      setError: 'alert/setError',
      setDiagnosis: 'pcase/setDiagnosis'
    }),
    searchDiseasesAjax (query, callback) {
      return async () => {
        if (!query) {
          return
        }
        try {
          const params = {
            keyword: query,
            limit: 15
          }
          const response = await this.axios.get('/api/search/disease-codes', {
            params
          })
          callback(response.data.data)
        } catch (err) {
          this.setError(err)
        }
      }
    },
    async create () {
      try {
        const params = { type: this.diagnosisType }
        const response = await this.axios.post(
          `/api/patient-cases/${this.value}/diagnoses`,
          params
        )
        this.setDiagnosis({ id: this.value, diagnosis: response.data.data })
        this.diagnosisId = response.data.data.id
        this.diagnosisType = response.data.data.type
      } catch (err) {
        this.setError(err)
      }
    },
    async saveComment (diagnosisId) {
      if (!this.comment.trim()) {
        return
      }
      if (!diagnosisId) {
        await this.create()
      }
      try {
        const params = { comment: this.comment }
        const response = await this.axios.post(
          `/api/patient-cases/${this.value}/diagnoses/${this.diagnosisId}/comment`,
          params
        )
        this.setDiagnosis({ id: this.value, diagnosis: response.data.data })
        if (this.diagnosis) {
          this.comment = this.diagnosis.comment
        }
      } catch (err) {
        this.setError(err)
      }
    },
    async addDisease (diagnosisId, code) {
      if (!diagnosisId) {
        await this.create()
      }
      try {
        const params = { code: code }
        const response = await this.axios.post(
          `/api/patient-cases/${this.value}/diagnoses/${this.diagnosisId}/conditions/add`,
          params
        )
        this.setDiagnosis({ id: this.value, diagnosis: response.data.data })
        this.keyword = null
      } catch (err) {
        this.setError(err)
      }
    },
    async removeDisease (diagnosisId, disease) {
      try {
        const params = { code: disease }
        const response = await this.axios.post(
          `/api/patient-cases/${this.value}/diagnoses/${diagnosisId}/conditions/remove`,
          params
        )
        this.setDiagnosis({ id: this.value, diagnosis: response.data.data })
      } catch (err) {
        this.setError(err)
      }
    },
    async publish (id) {
      try {
        const params = { type: this.diagnosisType }
        const response = await this.axios.post(
          `/api/patient-cases/${this.value}/diagnoses/${id}/publish`,
          params
        )
        this.setDiagnosis({ id: this.value, diagnosis: response.data.data })
      } catch (err) {
        this.setError(err)
      }
    }
  }
}
</script>
<style lang="scss">
.diagnosis {
  .probable-conditions {
    .probable-condition {
      z-index: 11;
      position: relative;
      .badge {
        width: 35px;
      }
    }
    .progress-bar {
      height: 100%;
      position: absolute;
      left: 0;
      background-color: #3c88ff55;
      top: 0;
      z-index: 10;
    }
  }
  .disease-list {
    max-height: 400px;
    overflow: auto;
  }
  .create-btn {
    color: #fff;
  }
  .list-item {
    display: block;
    // border-top: 1px solid #ccc;
    color: #555;
    text-decoration: none;
    svg {
      transition: all 0.2s;
      width: 24px;
      height: 24px;
    }
    .case-open {
      transform: rotate(180deg);
    }
  }
}
</style>
<i18n>
{
  "ka": {
    "diagnosis": "დიაგნოზი",
    "createPrediagnosis": "წინასწარი დიაგნოზის შექმნა",
    "createDiagnosis": "საბოლოო დიაგნოზი",
    "preliminary": "წინასწარი",
    "final": "საბოლოო",
    "condition": "დაავადება",
    "conditions": "დაავადებები",
    "comment": "დასკვნა",
    "save": "დამატება",
    "publish": "გამოქვეყნება",
    "delete": "წაშლა",
    "search": "ძიება",
    "addDisease": "დაავადების დამატება",
    "name": "დასახელება",
    "save": "შენახვა"
  },
  "en": {
    "diagnosis": "Diagnosis",
    "createPrediagnosis": "Create Preliminary Diagnosis",
    "createDiagnosis": "Create Diagnosis",
    "preliminary": "Preliminary Diagnosis",
    "final": "Final",
    "condition": "Condition",
    "conditions": "Conditions",
    "comment": "Comment",
    "save": "Save",
    "publish": "Publish",
    "delete": "Delete",
    "search": "Search",
    "addDisease": "Add Disease",
    "name": "Name",
    "save": "Save"
  }
}
</i18n>
